import { listItem } from 'prosemirror-schema-list';
import { schema } from 'prosemirror-markdown';
import DOMPurify from 'dompurify';

import { Schema } from 'prosemirror-model';

/**
 * Builds the message schema with node attr defaults matching the page's
 * current text direction, so freshly created nodes (e.g. a schema default
 * paragraph inserted without explicit attrs) inherit the right direction
 * instead of always falling back to ltr/left.
 * @param {string} defaultDirection - Current page direction ('ltr' or 'rtl')
 */
export const createMessageSchema = (defaultDirection = 'ltr') => {
  const defaultAlign = defaultDirection === 'rtl' ? 'right' : 'left';

  return new Schema({
    nodes: {
      doc: schema.spec.nodes.get('doc'),
      paragraph: {
        content: 'inline*',
        group: 'block',
        attrs: {
          align: { default: defaultAlign },
          direction: { default: defaultDirection },
          autoDetected: { default: true },
        },
        parseDOM: [
          {
            tag: 'p',
            getAttrs(dom) {
              const attrs = {};
              if (dom.style.textAlign) attrs.align = dom.style.textAlign;
              if (dom.style.direction) attrs.direction = dom.style.direction;
              return attrs;
            },
          },
        ],
        toDOM(node) {
          const { align, direction } = node.attrs;
          const styles = [];

          if (align) {
            styles.push(`text-align: ${align}`);
          }
          if (direction) {
            styles.push(`direction: ${direction}`);
          }

          if (styles.length > 0) {
            return ['p', { style: styles.join('; ') }, 0];
          }
          return ['p', {}, 0];
        },
      },
      blockquote: schema.spec.nodes.get('blockquote'),
      code_block: schema.spec.nodes.get('code_block'),
      text: schema.spec.nodes.get('text'),
      hard_break: schema.spec.nodes.get('hard_break'),
      image: {
        ...schema.spec.nodes.get('image'),
        attrs: {
          ...schema.spec.nodes.get('image').attrs,
          height: { default: null },
        },
        parseDOM: [
          {
            tag: 'img[src]',
            getAttrs: dom => ({
              src: dom.getAttribute('src'),
              title: dom.getAttribute('title'),
              alt: dom.getAttribute('alt'),
              height: parseInt(dom.style.height, 10),
            }),
          },
        ],
        toDOM: node => {
          const attrs = {
            src: node.attrs.src,
            alt: node.attrs.alt,
            height: node.attrs.height,
          };
          if (node.attrs.height) {
            attrs.style = `height: ${node.attrs.height}`;
          }
          return ['img', attrs];
        },
      },
      ordered_list: {
        content: 'list_item+',
        group: 'block',
        attrs: { order: { default: 1 }, dir: { default: defaultDirection } },
        parseDOM: [
          {
            tag: 'ol',
            getAttrs(dom) {
              const start = dom.getAttribute('start');
              return {
                order: start ? +start : 1,
                dir: dom.getAttribute('dir') || defaultDirection,
              };
            },
          },
        ],
        toDOM(node) {
          const attrs = {};
          if (node.attrs.order !== 1) attrs.start = node.attrs.order;
          if (node.attrs.dir) attrs.dir = node.attrs.dir;
          return ['ol', attrs, 0];
        },
      },
      bullet_list: {
        content: 'list_item+',
        group: 'block',
        attrs: { dir: { default: defaultDirection } },
        parseDOM: [
          {
            tag: 'ul',
            getAttrs(dom) {
              return { dir: dom.getAttribute('dir') || defaultDirection };
            },
          },
        ],
        toDOM(node) {
          if (node.attrs.dir) {
            return ['ul', { dir: node.attrs.dir }, 0];
          }
          return ['ul', 0];
        },
      },
      list_item: Object.assign(listItem, { content: 'paragraph block*' }),
      mention: {
        attrs: { userFullName: { default: '' }, userId: { default: '' } },
        group: 'inline',
        inline: true,
        selectable: true,
        draggable: true,
        atom: true,
        toDOM: node => [
          'span',
          {
            class: 'prosemirror-mention-node',
            'mention-user-id': node.attrs.userId,
            'mention-user-full-name': node.attrs.userFullName,
          },
          `@${node.attrs.userFullName}`,
        ],
        parseDOM: [
          {
            tag: 'span[mention-user-id][mention-user-full-name]',
            getAttrs: dom => {
              const userId = dom.getAttribute('mention-user-id');
              const userFullName = dom.getAttribute('mention-user-full-name');
              return { userId, userFullName };
            },
          },
        ],
      },
      html_block: {
        group: 'block',
        atom: true,
        selectable: false,
        attrs: { html: { default: '' } },
        parseDOM: [
          {
            tag: 'div[data-html-block]',
            getAttrs: dom => ({ html: dom.innerHTML }),
          },
        ],
        toDOM(node) {
          const dom = document.createElement('div');
          dom.setAttribute('data-html-block', 'true');
          dom.innerHTML = DOMPurify.sanitize(node.attrs.html);
          return dom;
        },
      },
    },
    marks: {
      link: schema.spec.marks.get('link'),
      em: schema.spec.marks.get('em'),
      strong: schema.spec.marks.get('strong'),
      code: schema.spec.marks.get('code'),
      strike: {
        parseDOM: [
          { tag: 's' },
          { tag: 'del' },
          { tag: 'strike' },
          {
            style: 'text-decoration',
            getAttrs: value => value === 'line-through',
          },
        ],
        toDOM: () => ['s', 0],
      },
      fontSize: {
        attrs: { size: {} },
        parseDOM: [
          {
            style: 'font-size',
            getAttrs: value => ({ size: value.replace('px', '') }),
          },
          {
            tag: 'span[style]',
            getAttrs: dom => {
              const fontSize = dom.style.fontSize;
              return fontSize ? { size: fontSize.replace('px', '') } : false;
            },
          },
        ],
        toDOM(mark) {
          return ['span', { style: `font-size: ${mark.attrs.size}px` }, 0];
        },
      },
      fontFamily: {
        attrs: { family: {} },
        parseDOM: [
          {
            style: 'font-family',
            getAttrs: value => ({ family: value }),
          },
          {
            tag: 'span[style]',
            getAttrs: dom => {
              const fontFamily = dom.style.fontFamily;
              return fontFamily ? { family: fontFamily } : false;
            },
          },
        ],
        toDOM(mark) {
          return ['span', { style: `font-family: ${mark.attrs.family}` }, 0];
        },
      },
      textColor: {
        attrs: { color: {} },
        parseDOM: [
          {
            style: 'color',
            getAttrs: value => ({ color: value }),
          },
          {
            tag: 'span[style]',
            getAttrs: dom => {
              const color = dom.style.color;
              return color ? { color } : false;
            },
          },
        ],
        toDOM(mark) {
          return ['span', { style: `color: ${mark.attrs.color}` }, 0];
        },
      },
    },
  });
};

export const messageSchema = createMessageSchema('ltr');
